import { useState, useRef, useEffect } from 'react';
import { Button, FormControl, InputLabel, Select, MenuItem, CircularProgress, Alert } from '@mui/material';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { PerspectiveCorrectionWithCrop } from '../components/PerspectiveCorrectionWithCrop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ImageIcon from '@mui/icons-material/Image';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { getChapters, getBooks, getBookRecords } from '../services/api';
import { filterHiddenChapters } from '../data/hiddenChapters';
import { useCorrectionStatus } from '../hooks/useCorrectionStatus';
import { useCorrection } from '../hooks/useCorrection';
import { getUserData } from '../utils/userData';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { Chapter } from '../types/common';
import ReactMarkdown from 'react-markdown';

interface CorrectionResult {
  _id: string;
  result?: string;
  error?: string;
  loading?: boolean;
  imageUrl?: string;
}

export default function ImageCropTest() {
  const navigate = useNavigate();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [processedImages, setProcessedImages] = useState<string[]>([]);
  const [processedBlobs, setProcessedBlobs] = useState<Blob[]>([]);
  const [correctionResults, setCorrectionResults] = useState<CorrectionResult[]>([]);
  const [chapterOptions, setChapterOptions] = useState<Chapter[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<string>('');
  const [isLoadingChapters, setIsLoadingChapters] = useState(false);
  const [error, setError] = useState<string>('');
  const [currentBookId, setCurrentBookId] = useState<string>('');
  const [lastSubmittedTicket, setLastSubmittedTicket] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 使用認證 hook
  const { isAuthenticated, user, isLoading } = useAuth();

  // 使用批改 hook
  const { submitCorrection } = useCorrection();

  // 使用批改狀態 hook
  const {
    correctionTickets,
    isProcessing
  } = useCorrectionStatus({
    studentId: getUserData()?.sub || null,
    enablePolling: !!lastSubmittedTicket,
    onCompleted: async (completedTickets) => {
      const userData = getUserData();
      if (!userData?.sub || !currentBookId) return;

      // 獲取批改記錄來取得詳細結果
      try {
        const recordsResponse = await getBookRecords(currentBookId, userData.sub, undefined);
        
        if (recordsResponse.data && Array.isArray(recordsResponse.data)) {
          // 找到對應的記錄（通常是最新的）
          const latestRecord = recordsResponse.data
            .filter(record => completedTickets.includes(record.ticket))
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
          
          if (latestRecord && latestRecord.correctingResult) {
            setCorrectionResults([{
              _id: latestRecord._id,
              loading: false,
              result: latestRecord.correctingResult,
              imageUrl: processedImages[0]
            }]);
          } else {
            setCorrectionResults([{
              _id: completedTickets[0],
              loading: false,
              result: '批改已完成，但無法載入詳細結果。',
              imageUrl: processedImages[0]
            }]);
          }
        }
      } catch (error) {
        console.error('載入批改結果失敗:', error);
        setCorrectionResults([{
          _id: completedTickets[0],
          loading: false,
          result: '批改已完成，但載入結果時發生錯誤。',
          imageUrl: processedImages[0]
        }]);
      }
      
      setLastSubmittedTicket('');
    },
    onFailed: (failedTickets) => {
      if (failedTickets.length > 0) {
        setCorrectionResults([{
          _id: failedTickets[0]._id,
          loading: false,
          error: failedTickets[0].status,
          imageUrl: processedImages[0]
        }]);
        setLastSubmittedTicket('');
      }
    }
  });

  // 用戶認證檢查
  useEffect(() => {
    if (isLoading) return; // 等待認證狀態載入完成
    
    if (!isAuthenticated) {
      navigate("/");
      return;
    }

    // 認證成功後載入書籍
    loadAvailableBooks();
  }, [isAuthenticated, isLoading, navigate]);


  // 載入可用的書籍
  const loadAvailableBooks = async () => {
    try {
      const response = await getBooks();
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const firstBook = response.data[0];
        setCurrentBookId(firstBook._id);
        // 載入該書籍的章節
        loadChaptersForBook(firstBook._id);
      } else {
        setError('沒有找到可用的書籍');
      }
    } catch (error) {
      console.error('載入書籍失敗:', error);
      setError('載入書籍失敗，請重新整理頁面');
    }
  };

  // 為特定書籍載入章節
  const loadChaptersForBook = async (bookId: string) => {
    setIsLoadingChapters(true);
    try {
      const userData = getUserData();
      
      const response = await getChapters(bookId, userData?.sub);
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const visibleChapters = filterHiddenChapters(response.data);
        setChapterOptions(visibleChapters);
        setSelectedChapter(visibleChapters[0]?._id ?? '');
        setError(''); // 清除錯誤訊息
      } else {
        setError('該書籍沒有可用的題目');
      }
    } catch (error) {
      console.error("載入章節失敗:", error);
      setError(`載入題目失敗：${error instanceof Error ? error.message : '未知錯誤'}`);
    } finally {
      setIsLoadingChapters(false);
    }
  };


  // 處理圖片上傳
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setSelectedImage(event.target?.result as string);
      setProcessedImages([]);
      setProcessedBlobs([]);
      setCorrectionResults([]);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  // 開始編輯
  const startEditing = () => {
    if (selectedImage) {
      setShowEditor(true);
    }
  };

  // 處理編輯完成
  const handleEditComplete = (images: Blob[]) => {
    // 反轉圖片順序，因為中文作文是從右到左書寫
    const reversedImages = [...images].reverse();
    const imageUrls = reversedImages.map(blob => URL.createObjectURL(blob));
    setProcessedImages(imageUrls);
    setProcessedBlobs(reversedImages);
    setShowEditor(false);
    setCorrectionResults([]); // 清空批改結果，確保顯示開始批改按鈕
  };

  // 取消編輯
  const handleEditCancel = () => {
    setShowEditor(false);
  };

  // 開始批改流程
  const startCorrection = async (images: Blob[]) => {
    if (!isAuthenticated || !selectedChapter) {
      setError('請先選擇章節');
      return;
    }


    // 初始化批改結果狀態 - 設為處理中
    setCorrectionResults([{
      _id: 'batch_correction',
      loading: true,
      imageUrl: processedImages[0]
    }]);

    // 找到對應的章節資料
    const selectedChapterData = chapterOptions.find(ch => ch._id === selectedChapter);
    const chapterId = selectedChapterData?.chapter || "1";

    try {
      // 將所有 blob 按順序轉換為 File 數組
      const files = images.map((blob, index) => 
        new File([blob], `cropped-image-${index + 1}.jpg`, { type: 'image/jpeg' })
      );
      
      // 單次批改API調用，按順序發送所有圖片
      const result = await submitCorrection({
        bookId: currentBookId,
        chapterId,
        classCode: "", // ImageCropTest 頁面暫時使用空值
        className: "", // ImageCropTest 頁面暫時使用空值
        studentInput: "", // 圖片模式不需要文字輸入
        files // 所有圖片按順序一次發送
      });

      // 設定票據ID以開始輪詢
      setLastSubmittedTicket(result.ticket || result._id);
      
      // 更新批改結果狀態
      setCorrectionResults([{
        _id: result._id,
        loading: true,
        imageUrl: processedImages[0]
      }]);

    } catch (error: any) {
      console.error('批次批改失敗:', error);
      setCorrectionResults([{
        _id: 'batch_correction',
        loading: false,
        error: error.response?.data?.detail || error.message || '批改失敗',
        imageUrl: processedImages[0]
      }]);
    }
  };

  // 下載處理後的圖片
  const downloadImage = (imageUrl: string, index: number) => {
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `processed-image-${index + 1}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 如果正在載入或未認證，顯示載入中
  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-100">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <CircularProgress />
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <Header />
      
      <div className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* 標題 */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              圖片矩形校正 + 分割 + AI批改測試
            </h1>
            <p className="text-gray-600 mb-2">
              上傳圖片，進行透視校正，然後選擇性地將圖片分割成多個部分並進行AI批改
            </p>
            <p className="text-sm text-blue-600">
              歡迎，{user?.profile?.name || "學生用戶"}！
            </p>
          </div>

          {/* 錯誤提示 */}
          {error && (
            <Alert severity="error" className="mb-6" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* 章節選擇 */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">選擇作文題目</h2>
            <FormControl fullWidth disabled={isLoadingChapters}>
              <InputLabel>請選擇題目</InputLabel>
              <Select
                value={selectedChapter}
                label="請選擇題目"
                onChange={(e) => setSelectedChapter(e.target.value)}
              >
                {chapterOptions.map((chapter) => (
                  <MenuItem key={chapter._id} value={chapter._id}>
                    {chapter.chapter}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {isLoadingChapters && (
              <div className="flex items-center justify-center mt-2">
                <CircularProgress size={20} />
                <span className="ml-2 text-sm text-gray-500">載入章節中...</span>
              </div>
            )}
          </div>

          {/* 上傳區域 */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                ref={fileInputRef}
                className="hidden"
              />
              
              {!selectedImage ? (
                <div>
                  <UploadFileIcon className="text-gray-400 text-6xl mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    上傳圖片開始測試
                  </h3>
                  <p className="text-gray-500 mb-4">
                    支援 JPG、PNG 等格式
                  </p>
                  <Button
                    variant="contained"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<UploadFileIcon />}
                  >
                    選擇檔案
                  </Button>
                </div>
              ) : (
                <div>
                  <img
                    src={selectedImage}
                    alt="原始圖片"
                    className="max-w-full max-h-64 mx-auto mb-4 rounded-lg shadow"
                  />
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="contained"
                      onClick={startEditing}
                      startIcon={<ImageIcon />}
                    >
                      開始編輯
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => fileInputRef.current?.click()}
                      startIcon={<UploadFileIcon />}
                    >
                      重新上傳
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* 處理結果與批改結果 */}
          {processedImages.length > 0 && (
            <div className="space-y-6">
              {/* 分割後的圖片顯示區塊 */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <ImageIcon className="text-blue-600" />
                    <h2 className="text-xl font-semibold text-gray-800">
                      分割結果 ({processedImages.length} 張圖片)
                    </h2>
                  </div>
                  <div className="text-sm text-gray-500">
                    ✓ 已按中文書寫順序排列 (右→左)
                  </div>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {processedImages.map((imageUrl, index) => (
                    <div key={index} className="text-center">
                      <h3 className="text-sm font-medium text-gray-600 mb-2">
                        第 {index + 1} 段 (右→左排序)
                      </h3>
                      <img
                        src={imageUrl}
                        alt={`作文第 ${index + 1} 段`}
                        className="w-full h-32 object-contain rounded-lg border bg-gray-50 mb-2"
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => downloadImage(imageUrl, index)}
                      >
                        下載第 {index + 1} 段
                      </Button>
                    </div>
                  ))}
                </div>

                {/* 開始批改按鈕 */}
                {correctionResults.length === 0 && (
                  <div className="text-center mt-6 pt-4 border-t border-gray-200">
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => startCorrection(processedBlobs)}
                      disabled={!selectedChapter || isProcessing}
                      startIcon={<AutoAwesomeIcon />}
                    >
                      {isProcessing ? '批改中...' : '開始批改分析'}
                    </Button>
                    {isProcessing && (
                      <p className="mt-2 text-sm text-gray-500">
                        請稍候，正在處理中...
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* 批改結果區塊 - 參考 Feedback 頁面設計 */}
              {correctionResults.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-800">
                      AI批改結果
                    </h2>
                  </div>

                  {/* 批改結果內容 */}
                  <div className="space-y-6">
                    {correctionResults.map((result, index) => (
                      <div key={index}>
                        {result?.loading || isProcessing ? (
                          <div className="flex flex-col items-center justify-center py-12">
                            <CircularProgress size={40} />
                            <p className="mt-4 text-gray-500">
                              {correctionTickets.length > 0 ? (
                                <>
                                  {correctionTickets[0].status === '圖片辨識中' ? '圖片辨識中' : '批改中'}，請稍候...
                                </>
                              ) : (
                                'AI批改中，請稍候...'
                              )}
                            </p>
                            {correctionTickets.length > 0 && (
                              <p className="mt-2 text-sm text-gray-400">
                                狀態：{correctionTickets[0].status}
                              </p>
                            )}
                          </div>
                        ) : result?.error ? (
                          <div className="text-center py-8">
                            <Alert severity="error" className="mb-4">
                              {result.error}
                            </Alert>
                            <Button
                              variant="contained"
                              onClick={() => startCorrection(processedBlobs)}
                              startIcon={<AutoAwesomeIcon />}
                            >
                              重新批改
                            </Button>
                          </div>
                        ) : result?.result ? (
                          <div className="space-y-6">
                            {/* 批改結果內容 - 使用 ReactMarkdown 渲染 */}
                            <div className="prose prose-sm max-w-none">
                              <ReactMarkdown
                                components={{
                                  h1: ({ children }) => <h1 className="text-2xl font-bold mb-4 text-gray-800">{children}</h1>,
                                  h2: ({ children }) => <h2 className="text-xl font-semibold mb-3 text-gray-800">{children}</h2>,
                                  h3: ({ children }) => <h3 className="text-lg font-semibold mb-2 text-blue-600">{children}</h3>,
                                  p: ({ children }) => <p className="mb-3 text-gray-700 leading-relaxed">{children}</p>,
                                  strong: ({ children }) => <strong className="font-semibold text-gray-800">{children}</strong>,
                                  em: ({ children }) => <em className="italic text-gray-700">{children}</em>,
                                  ul: ({ children }) => <ul className="list-disc list-inside mb-3 text-gray-700 space-y-1">{children}</ul>,
                                  ol: ({ children }) => <ol className="list-decimal list-inside mb-3 text-gray-700 space-y-1">{children}</ol>,
                                  li: ({ children }) => <li className="mb-1">{children}</li>,
                                  table: ({ children }) => (
                                    <div className="overflow-x-auto">
                                      <table className="min-w-full border-collapse border border-gray-300 mb-4">
                                        {children}
                                      </table>
                                    </div>
                                  ),
                                  thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
                                  th: ({ children }) => <th className="border border-gray-300 px-4 py-2 text-left font-semibold">{children}</th>,
                                  td: ({ children }) => <td className="border border-gray-300 px-4 py-2">{children}</td>,
                                  blockquote: ({ children }) => (
                                    <blockquote className="border-l-4 border-blue-400 pl-4 italic text-gray-600 my-4 bg-blue-50 py-2">
                                      {children}
                                    </blockquote>
                                  ),
                                }}
                              >
                                {result.result}
                              </ReactMarkdown>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <Footer />

      {/* 編輯器 */}
      {showEditor && selectedImage && (
        <PerspectiveCorrectionWithCrop
          imageUrl={selectedImage}
          onConfirm={handleEditComplete}
          onCancel={handleEditCancel}
        />
      )}
    </div>
  );
}